import { useEffect, useState } from "react";
import { UnifiedMessage } from "@/hooks/use-messages";
import { useGlobalState } from "@/hooks";
import { CartProduct, Product } from "@/types/product";
import { productToCartProduct } from "@/utils/product-to-cart-product";
import CartItemSmall from "./CartItemSmall";
import FlatVoiceBlock from "./FlatVoiceBlock";
import CircleVoiceBlock from "./CircleVoiceBlock";
import CircleSilenceBlock from "./CircleSilenceBlock";
import { ProductDetails } from "./ProductDetails";
import { LastUserMessage } from "./LastUserMessage";
import { ProductList } from "./ProductList";

const INTRODUCTION_HTML_ASK_MICROPHONE_PERMISSION = "Enable mic to continue";
const INTRODUCTION_HTML = {
  __html:
    "Connecting to your personal assistant… <br/>Please hold on while we prepare everything for you.",
};

type VoiceModeProps = {
  messages: UnifiedMessage[];
  agentState: string;
  productList: Product[];
  productDescription: Product;
  setProductDescription: (
    product: Product | ((prevProduct: Product) => Product) | null,
  ) => void;
  setCartItems: (
    products: CartProduct[] | ((prevProducts: CartProduct[]) => CartProduct[]),
  ) => void;
  cartItems: CartProduct[];
  onAddToCart: (product: Product) => void;
};

export const VoiceMode = ({
  messages,
  agentState,
  productList,
  productDescription,
  setProductDescription,
  cartItems,
  setCartItems,
  onAddToCart,
}: VoiceModeProps) => {
  const { chatMode, micPermission, setMicPermission } = useGlobalState();
  const [lastUserMessage, setLastUserMessage] = useState<string>("");

  // Set a listener for microphone permission changes
  useEffect(() => {
    let permissionStatus: PermissionStatus;

    // Observe Permissions in non-iOS devices (not supported on iOS)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "microphone" as PermissionName })
        .then((status) => {
          permissionStatus = status;
          setMicPermission(status.state);

          status.onchange = () => {
            console.log("Microphone permission changed:", status.state);
            setMicPermission(status.state);
          };
        });

      return () => {
        if (permissionStatus) {
          permissionStatus.onchange = null;
        }
      };
    }
  }, [setMicPermission]);

  useEffect(() => {
    const userMessages = messages.filter((message) => {
      const isFromUser = message.isUser || message.isSelf;
      const isProductMessage =
        message.message?.startsWith("products_") &&
        message.text?.startsWith("products_");

      return isFromUser && !isProductMessage;
    });

    if (userMessages.length > 0) {
      const userMessageAtLastIndex = userMessages[userMessages.length - 1];
      const messageContent =
        userMessageAtLastIndex.text || userMessageAtLastIndex.message || "";

      setLastUserMessage(messageContent);
    }
  }, [chatMode, messages]);

  const handleYesAddToCart = () => {
    setCartItems((prevItems) => [
      ...prevItems,
      productToCartProduct(productDescription),
    ]);
    onAddToCart(productDescription);
    setTimeout(() => {
      setProductDescription(null);
    }, 300);
  };

  return (
    <>
      {(productList?.length > 0 || productDescription) && (
        <FlatVoiceBlock isActive={agentState === "speaking" ? true : false} />
      )}
      {lastUserMessage && <LastUserMessage text={lastUserMessage} />}
      <div
        className={`chat-body flex-1 overflow-y-auto flex flex-col items-center ${productList.length > 0 || productDescription ? "justify-start" : "justify-center pt-[20px]"} pb-[20px]`}
      >
        {micPermission !== "granted" && (
          <>
            <p className="text-chat-introduction text-center text-[22px] leading-[150%] tracking-[0%] align-middle">
              {INTRODUCTION_HTML_ASK_MICROPHONE_PERMISSION}
            </p>
            <br />
          </>
        )}

        {agentState === "connecting" && (
          <p
            className="text-chat-introduction text-center text-[18px] leading-[150%] tracking-[0%] align-middle"
            dangerouslySetInnerHTML={INTRODUCTION_HTML}
          ></p>
        )}

        {productList.length === 0 && agentState === "speaking" && (
          <CircleVoiceBlock />
        )}
        {productList.length === 0 &&
          ["listening", "thinking"].includes(agentState) && (
            <CircleSilenceBlock />
          )}

        {/* product */}
        {productList?.length > 0 && !productDescription && (
          <ProductList
            setProductDescription={setProductDescription}
            productList={productList}
          />
        )}

        {/* show product description */}
        {productDescription && (
          <>
            <div className="widget-displaying-results w-[100%]">
              <ProductDetails
                image={productDescription?.images?.[0]?.src}
                title={productDescription.title}
                currentPrice={productDescription?.variants?.[0]?.price}
                onYesClick={handleYesAddToCart}
                onNoClick={() => {
                  setProductDescription(null);
                }}
                isAddedToCart={cartItems.some(
                  (item) =>
                    item.variant_id === productDescription?.variants?.[0]?.id,
                )}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
